import React, {
  MouseEvent,
  MutableRefObject,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
} from 'react';
import ReactDOM from 'react-dom';
import {
  Placement,
  autoUpdate,
  computePosition,
  flip,
  shift,
  size,
} from '@floating-ui/react';
import styled from 'astroturf/react';

import useOnClickOutside from 'js/utils/hooks/useOnClickOutside';
// This import is in JSDoc.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Menu } from './Menu';

import { ModalElementContext } from './Modal';
import _ActivatorContainer from "Popover-ActivatorContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/design_system/Popover.tsx?styleId=ActivatorContainer";
import _PopoverContainer from "Popover-PopoverContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/design_system/Popover.tsx?styleId=PopoverContainer";


const ActivatorContainer = /*#__PURE__*/styled("div", null, {
  displayName: "ActivatorContainer",
  styles: _ActivatorContainer
});

const PopoverContainer = /*#__PURE__*/styled("div", null, {
  displayName: "PopoverContainer",
  styles: _PopoverContainer
});

export enum PopoverMode {
  Click = 'click',
  Hover = 'hover',
  Any = 'any',
}

export enum PopoverWidthMode {
  Activator = 'activator',
  Content = 'content',
}

export type PopoverProps = {
  /**
   * Controlled component toggle. Determines if the popover should currently show.
   */
  open?: boolean;
  /**
   * Uncontrolled component options.
   * {@link PopoverMode.Click} shows the popover after clicking the activator.
   * {@link PopoverMode.Hover} shows the popover when hovering over the activator.
   * {@link PopoverMode.Any} shows the popover after clicking or hovering the activator.
   */
  mode?: PopoverMode;
  /**
   * How the popover should determine its size.
   * {@link PopoverWidthMode.Activator} match the width of the activator.
   * {@link PopoverWidthMode.Content} match the width of the content.
   */
  widthMode?: PopoverWidthMode;
  /**
   * Where the popover should by default, relative to the activator.
   * See {@link Placement} for more details.
   */
  placement?: Placement;
  /**
   * Convenience prop to keep the activator inline with the rest of the content.
   */
  inline?: boolean;
  /**
   * How far away the popover should be from the activator in pixels.
   */
  offset?: number;
  /**
   * Whether to allow the popover to show.
   */
  disabled?: boolean;
  /**
   * The maximum width for the popover, in pixels.
   */
  maxWidth?: number;
  /**
   * The maximum height for the popover, in pixels.
   */
  maxHeight?: number;
  /**
   * Whether the popover should close when clicked. This option is useful
   * if you want the menu to close only when clicking outside.
   */
  closeOnClick?: boolean;
  /**
   * Reference to the popover.
   */
  popoverRef?: MutableRefObject<HTMLDivElement | null>;
  /**
   * Class names for the activator.
   */
  className?: string;
  /**
   * Styles for the activator.
   */
  style?: React.CSSProperties;
  /**
   * Class names for the popover container.
   */
  containerClassName?: string;
  /**
   * Styles for the popover container.
   */
  containerStyle?: React.CSSProperties;
  /**
   * The popover activator. Depending on the popover mode,
   * the popover will appear when hovering/clicking on these children.
   */
  children: ReactNode;
  /**
   * The element to render the popover in. It's usually fine to
   * leave this out and just use document.body, but you might need
   * this to render inside of a modal or other container.
   */
  portalBasis?: HTMLElement | null;
  /**
   * Render callback for the popover. A callback is better than
   * a simple {@link ReactNode} because it allows lazy rendering
   * if the popover content is complex or fetches data.
   */
  render: () => ReactNode;
  /**
   * Callback when the menu changes open state.
   * @param open The updated open state.
   */
  onChange?: (open: boolean) => void;
  /**
   * Disable automatic flipping to keep popover in viewport.
   */
  disableFlip?: boolean;
  /**
   * Disable automatic shifting to keep popover in viewport.
   */
  disableShift?: boolean;
};

/**
 * A basic popover component with no opinionated styling.
 * It provides core popover functionality including placement,
 * common open/close mechanisms, and keeping the popover in the viewport.
 *
 * If you want to use a popover with Academia styles, see {@link Menu} instead.
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { Popover, PopoverMode } from 'js/design_system/Popover';
 *
 * <Popover
 *   mode={PopoverMode.Click}
 *   placement='bottom-start'
 *   offset={4}
 *   render={() => <p>I'm the popover content!</p>}
 * >
 *   <p>Click me to activate!</p>
 * </Popover>
 * ```
 */
export const Popover: React.FC<PopoverProps> = ({
  open: controlledOpen,
  mode = PopoverMode.Hover,
  widthMode = PopoverWidthMode.Content,
  placement = 'bottom',
  inline = false,
  offset = 0,
  disabled = false,
  disableFlip = false,
  disableShift = false,
  maxWidth,
  maxHeight,
  closeOnClick = false,
  popoverRef: propPopoverRef,
  className = '',
  style,
  containerClassName = '',
  containerStyle,
  portalBasis,
  children,
  render,
  onChange = () => null,
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const activatorRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const controlled = controlledOpen !== undefined;
  const modalElement = useContext(ModalElementContext)?.current;

  useEffect(() => {
    setIsClient(true);
  }, []);

  const handleClick =
    !controlled && (mode === PopoverMode.Click || mode === PopoverMode.Any);
  const handleHover =
    !controlled && (mode === PopoverMode.Hover || mode === PopoverMode.Any);

  const open = useMemo(() => {
    return controlledOpen ?? uncontrolledOpen;
  }, [controlledOpen, uncontrolledOpen]);

  const updatePosition = useCallback(async () => {
    if (!activatorRef.current || !popoverRef.current) return;

    const middleware = [
      ...(!disableFlip ? [flip()] : []),
      ...(!disableShift ? [shift({ crossAxis: true })] : []),
      size({
        apply({ availableWidth, availableHeight, rects }) {
          if (!popoverRef.current) return;

          const finalMaxWidth = Math.min(
            availableWidth,
            maxWidth ?? availableWidth,
          );
          const finalMaxHeight = Math.min(
            availableHeight,
            maxHeight ?? availableHeight,
          );
          Object.assign(popoverRef.current.style, {
            maxWidth: `${finalMaxWidth}px`,
            maxHeight: `${finalMaxHeight}px`,
          });
          if (widthMode === PopoverWidthMode.Activator) {
            const side = placement.split('-')[0];
            const width = ['top', 'bottom'].includes(side)
              ? `${rects.reference.width}px`
              : undefined;
            const height = ['left', 'right'].includes(side)
              ? `${rects.reference.height}px`
              : undefined;
            Object.assign(popoverRef.current.style, { width, height });
          }
        },
      }),
    ];
    const { x, y } = await computePosition(
      activatorRef.current,
      popoverRef.current,
      {
        placement,
        middleware,
      },
    );
    // due to the async call above, we need to check if the refs are still valid
    if (!popoverRef.current) return;
    Object.assign(popoverRef.current.style, {
      top: `${y}px`,
      left: `${x}px`,
    });
  }, [placement, widthMode, maxWidth, maxHeight, disableFlip, disableShift]);

  const updateOpen = useCallback(
    (update: boolean) => {
      setUncontrolledOpen(!disabled && update);
      onChange(!disabled && update);
    },
    [disabled, onChange],
  );

  useOnClickOutside([activatorRef, popoverRef], () => updateOpen(false));

  useEffect(() => {
    if (activatorRef.current && popoverRef.current) {
      return autoUpdate(
        activatorRef.current,
        popoverRef.current,
        updatePosition,
      );
    }
  }, [open, updatePosition]);

  useEffect(() => {
    if (disabled && open) {
      updateOpen(false);
    }
  }, [disabled, open, updateOpen]);

  const offsetStyle = useMemo((): React.CSSProperties => {
    const side = placement.split('-')[0];
    const paddingSide =
      {
        top: 'paddingBottom',
        right: 'paddingLeft',
        bottom: 'paddingTop',
        left: 'paddingRight',
      }[side] || 'paddingTop';
    return { [paddingSide]: offset };
  }, [placement, offset]);

  const handleActivatorClick = () => {
    if (handleClick) {
      updateOpen(!open);
    }
  };

  const handlePopoverClick = (event: MouseEvent) => {
    event.stopPropagation();
    if (closeOnClick) {
      updateOpen(false);
    }
  };

  const handleMouseEnter = () => {
    if (handleHover) {
      updateOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (handleHover) {
      updateOpen(false);
    }
  };

  return (
    <ActivatorContainer
      ref={activatorRef}
      inline={inline}
      className={className}
      style={style}
      onClick={handleActivatorClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}

      {isClient &&
        ReactDOM.createPortal(
          <PopoverContainer
            ref={(ref) => {
              popoverRef.current = ref;
              if (propPopoverRef) {
                propPopoverRef.current = ref;
              }
            }}
            className={containerClassName}
            style={{ ...containerStyle, ...offsetStyle }}
            onClick={handlePopoverClick}
          >
            {open && render()}
          </PopoverContainer>,
          portalBasis ?? modalElement ?? document.body,
        )}
    </ActivatorContainer>
  );
};
